"use client";

import {JSX} from "react";

import {useTranslation} from "@/shared/i18n";
import type {TranslationKey} from "@/shared/i18n/types";
import {DateHelper} from "@/shared/lib";
import {Typography} from "@/shared/ui/data-display/typography";
import {TRIP_TABS, type TripTab} from "@/shared/utils/routing";

import "./styles/trip-sidebar.scss";

export interface TripSidebarProps {
    unit: {
        locationLabel: string;
        unitName: string;
    };
    stay: {
        checkInDate: string;
        checkOutDate: string;
        checkInWeekday: string;
        checkOutWeekday: string;
        checkInTime: string;
        checkOutTime: string;
    };
    activeTab: TripTab;
    onSelectTab: (tab: TripTab) => void;
}

const NAV_ITEMS: Array<{tab: TripTab; labelKey: TranslationKey}> = [
    {tab: TRIP_TABS.overview, labelKey: "trip.navOverview"},
    {tab: TRIP_TABS.gettingThere, labelKey: "trip.navGettingThere"},
    {tab: TRIP_TABS.amenities, labelKey: "trip.navAmenities"},
    {tab: TRIP_TABS.terms, labelKey: "trip.navRentalTerms"},
    {tab: TRIP_TABS.pricing, labelKey: "trip.navPricing"},
    {tab: TRIP_TABS.cancel, labelKey: "trip.navCancel"},
];

export function TripSidebar({unit, stay, activeTab, onSelectTab}: TripSidebarProps): JSX.Element {
    const {t} = useTranslation();
    const dateRange = DateHelper.formatDateRangeWithYear(DateHelper.parseLocalDate(stay.checkInDate), DateHelper.parseLocalDate(stay.checkOutDate));

    return (
        <nav className="trip-sidebar" aria-label={t("trip.yourTrip")}>
            <div className="trip-sidebar__header">
                <Typography as="span" size={14} weight={400} color="paragraph2" className="trip-sidebar__eyebrow" options={["uppercase"]}>
                    {t("trip.yourTrip")}
                </Typography>
                <div className="trip-sidebar__dates">
                    <Typography as="p" size={16} weight={600} className="trip-sidebar__guest">
                        {unit.unitName}
                    </Typography>
                    <Typography as="p" size={14} color="paragraph2">
                        {dateRange}
                    </Typography>
                </div>
            </div>

            <ul className="trip-sidebar__list">
                {NAV_ITEMS.map((item) => {
                    const isActive = activeTab === item.tab;
                    return (
                        <li key={item.tab}>
                            <button
                                type="button"
                                className={`trip-sidebar__link${isActive ? " trip-sidebar__link--active" : ""}`}
                                aria-current={isActive ? "page" : undefined}
                                onClick={() => onSelectTab(item.tab)}
                            >
                                {t(item.labelKey)}
                            </button>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
}
